// ── Publish Statements ──────────────────────────────────────────────────────
//
// One page for the job that was previously fifteen visits to fifteen account
// pages: look at every generated statement, check it, and make it visible to
// the borrower.
//
// GROUPED BY MONTH, THEN BY TYPE. That is the shape of the work — a servicer
// does not publish "a statement", they publish August's interest statements,
// then August's invoices. The bulk action therefore sits on the month+type
// group and nowhere else: a "publish everything on this page" button would span
// periods and document types at once, which is never a thing anyone means to do.
//
// PUBLISHING IS THE ONE ACTION HERE THAT A BORROWER SEES. Everything on this
// page is arranged around making that deliberate: the default filter shows only
// what is unpublished, the bulk confirm names the month, the type and the
// count, and the row carries a link to the actual file so "review" can mean
// reading it rather than trusting the filename.
//
// The row carries the same eye / pencil / bin as the Account page, over the same
// shared StatementModal — a record that can be corrected from one place and not
// the other is a record people learn to distrust. Publish stays a word in its
// own column rather than a fourth icon: it is the only control here a borrower
// feels, and it should never be one mis-click away from the bin.
import { useState, useEffect, useCallback } from "react";
import { B } from "../../../shared/theme";
import { fmtDate, localFilter } from "../../../shared/format";
import { Btn, Card, ErrorMsg, FilterSelect, Icon, SearchBar, SectionHeader, Spinner, Td, Th } from "../../../shared/ui";
import { StatementModal, generatedAt } from "../components/StatementModal";
import { useApp } from "../../../shared/appContext";


const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

// Read the month off the STRING rather than through a Date. '2026-08-31'
// through `new Date()` is parsed as UTC midnight and rendered in local time,
// which in any negative offset lands on 30 August and files a month-end
// statement under the wrong month. The substring cannot do that.
const monthKey = (d) => (d ? String(d).slice(0, 7) : "");
const monthLabel = (key) => {
  if (!key) return "No as-of date";
  const [y, m] = key.split("-");
  return `${MONTHS[Number(m) - 1] || m} ${y}`;
};

const StatusChip = ({ published }) => (
  <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 999, fontWeight: 600,
    background: published ? "#E1F5EE" : "#FAECE7", color: published ? "#0F6E56" : "#993C1D" }}>
    {published ? "Published" : "Not Published"}
  </span>
);

export const PublishStatementsPage = () => {
  const { api, role, go, setConfirm, delRec } = useApp();

  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState(null);
  const [q, setQ]             = useState("");
  const [typeFilter, setType] = useState("");
  // Defaults to everything. The page opens on the whole picture — what exists,
  // what is already out, what is still waiting — and narrowing to the backlog
  // is one click away. The unpublished count in the header carries that signal
  // without hiding the rest.
  const [status, setStatus]   = useState("all");
  const [busy, setBusy]       = useState(null);   // row id, or a group key
  const [open, setOpen]       = useState(null);   // { statement, mode }

  const load = useCallback(() => {
    setLoading(true); setErr(null);
    api.get("getAllStatements")
      .then((d) => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch((e) => { setErr(e.message); setLoading(false); });
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const setPublish = async (s, publish) => {
    setBusy(s.id);
    try { await api.post("updateStatement", { id: s.id, publish }); load(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(null); }
  };

  // Sequential rather than Promise.all. These are writes that change what
  // borrowers can see, and a partial failure needs to leave a clear line
  // between what was published and what was not — twelve parallel requests
  // failing halfway tells you neither.
  const publishGroup = async (key, list) => {
    setBusy(key); setErr(null);
    try {
      for (const s of list) await api.post("updateStatement", { id: s.id, publish: true });
      load();
    } catch (e) {
      setErr(`Stopped partway: ${e.message}. Some statements in this group may already be published.`);
      load();
    } finally { setBusy(null); }
  };

  const remove = (s) => setConfirm({
    msg: `Delete the ${s.Type || "statement"} record for ${s.companyname || s.accountid}`
       + `${s.As_of_Date ? " — " + fmtDate(s.As_of_Date) : ""}?`
       + (s.Publish ? " It is currently PUBLISHED and visible to the borrower." : "")
       + " The file itself stays in Box — only this record and its link are removed.",
    onConfirm: () => delRec('Statements', s.id,
      `Deleted statement record ${s.Type || ''} ${s.As_of_Date || ''}`.trim(), load),
  });

  const typeOptions = Array.from(new Set(rows.map((r) => r.Type).filter(Boolean))).sort();

  let shown = localFilter(rows, q, ["companyname", "accountid", "Type", "Note"]);
  if (typeFilter) shown = shown.filter((r) => r.Type === typeFilter);
  if (status === "unpublished") shown = shown.filter((r) => !r.Publish);
  if (status === "published")   shown = shown.filter((r) => !!r.Publish);

  // month -> type -> rows. Built off the FILTERED set, so a count on a heading
  // always describes what is underneath it.
  const months = [];
  const byMonth = new Map();
  for (const r of shown) {
    const mk = monthKey(r.As_of_Date);
    if (!byMonth.has(mk)) { byMonth.set(mk, new Map()); months.push(mk); }
    const types = byMonth.get(mk);
    const tk = r.Type || "—";
    if (!types.has(tk)) types.set(tk, []);
    types.get(tk).push(r);
  }
  // Newest month first; a row with no as-of date sorts last rather than first,
  // which is where an unfiled thing belongs.
  months.sort((a, b) => (a === "" ? 1 : b === "" ? -1 : (a < b ? 1 : a > b ? -1 : 0)));

  const unpublishedTotal = rows.filter((r) => !r.Publish).length;

  if (loading) return <Spinner />;
  if (err && !rows.length) return <ErrorMsg msg={err} onRetry={load} />;

  return <>
    <SectionHeader title="Publish Statements" actions={[
      <span key="n" style={{ fontSize: 12, color: "var(--color-text-secondary)", alignSelf: "center" }}>
        {unpublishedTotal === 0
          ? "Everything generated has been published."
          : `${unpublishedTotal} statement${unpublishedTotal === 1 ? "" : "s"} not yet visible to borrowers`}
      </span>,
      <Btn key="r" onClick={load}>Reload</Btn>,
    ]} />

    <Card style={{ padding: 14, marginBottom: 14 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <SearchBar value={q} onChange={setQ} placeholder="Company, account id, note…" />
        <FilterSelect label="Type" value={typeFilter} onChange={setType}
                      options={typeOptions} allLabel="All types" />
        <FilterSelect label="Status" value={status} onChange={setStatus} allLabel={null}
                      options={[{ value: "unpublished", label: "Not published" },
                                { value: "published",   label: "Published" },
                                { value: "all",         label: "All" }]} />
      </div>
    </Card>

    {err && rows.length > 0 && (
      <Card style={{ padding: "10px 14px", marginBottom: 14, background: "#FAECE7",
                     borderLeft: "3px solid #993C1D" }}>
        <span style={{ fontSize: 12, color: "#993C1D" }}>{err}</span>
      </Card>
    )}

    {shown.length === 0 && (
      <Card style={{ padding: 40, textAlign: "center", fontSize: 13, color: "var(--color-text-secondary)" }}>
        {status === "unpublished"
          ? <>Nothing is waiting to be published. Switch <strong>Status</strong> to <strong>All</strong> to see everything on file.</>
          : "No statements match these filters."}
      </Card>
    )}

    {months.map((mk) => {
      const types = byMonth.get(mk);
      const monthRows = [...types.values()].flat();
      const monthPending = monthRows.filter((r) => !r.Publish).length;

      return (
        <div key={mk || "none"} style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{monthLabel(mk)}</span>
            <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
              {monthRows.length} statement{monthRows.length === 1 ? "" : "s"}
              {monthPending > 0 ? ` · ${monthPending} not published` : ""}
            </span>
          </div>

          {[...types.keys()].sort().map((tk) => {
            // Within a type, by as-of date, then by company so the same
            // borrower is always in the same place down the page.
            const list = [...types.get(tk)].sort((a, b) => {
              const ad = String(a.As_of_Date || ""), bd = String(b.As_of_Date || "");
              if (ad !== bd) return ad < bd ? -1 : 1;
              return String(a.companyname || "").localeCompare(String(b.companyname || ""));
            });
            const pending = list.filter((r) => !r.Publish);
            const groupKey = mk + "|" + tk;

            return (
              <Card key={groupKey} style={{ marginBottom: 12, overflowX: "auto" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10,
                              flexWrap: "wrap", marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{tk}</span>
                  <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
                    {list.length} borrower{list.length === 1 ? "" : "s"}
                    {pending.length > 0 ? ` · ${pending.length} not published` : " · all published"}
                  </span>
                  {role.canEdit && pending.length > 0 && (
                    <div style={{ marginLeft: "auto" }}>
                      <Btn variant="primary" disabled={busy === groupKey}
                        onClick={() => setConfirm({
                          msg: `Publish ${pending.length} ${tk} statement${pending.length === 1 ? "" : "s"} `
                             + `for ${monthLabel(mk)}? They become visible to those borrowers in the client portal immediately.`,
                          onConfirm: () => publishGroup(groupKey, pending),
                        })}>
                        {busy === groupKey ? "Publishing…" : `Publish all ${pending.length}`}
                      </Btn>
                    </div>
                  )}
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
                  <thead><tr>
                    <Th>Company</Th>
                    <Th>As of Date</Th>
                    <Th>Doc</Th>
                    <Th>File</Th>
                    <Th>Generated</Th>
                    <Th>Note</Th>
                    <Th>Status</Th>
                    <Th></Th>{/* publish toggle */}
                    <Th></Th>{/* view / edit / delete */}
                  </tr></thead>
                  <tbody>
                    {list.map((s) => (
                      <tr key={s.id}>
                        <Td>
                          {s.companyname
                            ? <a href="#" onClick={(e) => { e.preventDefault();
                                  go("account", { accountid: s.accountid, companyname: s.companyname }); }}
                                 style={{ color: B.cyan, textDecoration: "none" }}>{s.companyname}</a>
                            /* The account is gone but the statement — possibly a
                               published one — is not. Naming the orphan is the
                               whole reason this page LEFT JOINs. */
                            : <span style={{ color: "#993C1D" }}>
                                no account record ({s.accountid || "no id"})
                              </span>}
                        </Td>
                        <Td>{s.As_of_Date ? fmtDate(s.As_of_Date) : "—"}</Td>
                        <Td style={{ textTransform: "uppercase", fontSize: 12 }}>{s.Doc_Type || "—"}</Td>
                        <Td>{s.Box_URL
                          ? <a href={s.Box_URL} target="_blank" rel="noreferrer" style={{ color: B.cyan }}>Open</a>
                          : <span style={{ color: "#BA7517" }}>no link</span>}</Td>
                        <Td>{generatedAt(s) ? fmtDate(generatedAt(s)) : "—"}</Td>
                        <Td style={{ whiteSpace: "normal", maxWidth: 260 }}>{s.Note || "—"}</Td>
                        <Td><StatusChip published={!!s.Publish} /></Td>
                        <Td>
                          {role.canEdit && (
                            <Btn onClick={() => setPublish(s, !s.Publish)} disabled={busy === s.id}>
                              {busy === s.id ? "…" : (s.Publish ? "Unpublish" : "Publish")}
                            </Btn>
                          )}
                        </Td>
                        <Td><div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <Btn onClick={() => setOpen({ statement: s, mode: "view" })}><Icon type="view" /></Btn>
                          {role.canEdit && <Btn onClick={() => setOpen({ statement: s, mode: "edit" })}><Icon type="edit" /></Btn>}
                          {role.canDelete && <Btn variant="danger" onClick={() => remove(s)}><Icon type="delete" /></Btn>}
                        </div></Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            );
          })}
        </div>
      );
    })}

    {open && <StatementModal statement={open.statement} mode={open.mode} api={api}
                             onClose={() => setOpen(null)} onSaved={load} />}
  </>;
};
